import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import type { GalleryImage } from '../types';

interface Props {
  images: GalleryImage[];
}

function GalleryCard({ src, alt, caption, label, index }: GalleryImage & { index: number }) {
  const { ref, isVisible } = useScrollAnimation(0.05);
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative overflow-hidden rounded-2xl cursor-pointer group transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${index === 0 ? 'sm:col-span-2 sm:row-span-2' : ''}`}
      style={{ transitionDelay: `${index * 80}ms`, height: index === 0 ? '420px' : '200px' }}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover object-top transition-transform duration-700"
        style={{ transform: hovered ? 'scale(1.06)' : 'scale(1)' }}
      />

      {/* Overlay */}
      <div
        className="absolute inset-0 transition-opacity duration-400"
        style={{
          background: 'linear-gradient(to top, rgba(10,22,40,0.85) 0%, rgba(10,22,40,0.2) 60%, transparent 100%)',
          opacity: hovered ? 1 : 0.7,
        }}
      />

      {/* Label badge */}
      <div className="absolute top-3 left-3">
        <span className="bg-[#C8A96A] text-black text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
          {label}
        </span>
      </div>

      {/* Caption */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 transition-transform duration-400"
        style={{ transform: hovered ? 'translateY(0)' : 'translateY(8px)' }}
      >
        <p className="text-white font-playfair font-semibold text-sm md:text-base leading-snug">{caption}</p>
        <div className="flex items-center gap-1.5 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-4 h-0.5 bg-[#C8A96A] rounded-full" />
          <span className="text-[#C8A96A] text-xs">Quantum Electrical · Sleaford</span>
        </div>
      </div>
    </div>
  );
}

export default function ServiceGallery({ images }: Props) {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  return (
    <section className="py-20 bg-[#F9F5EE] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-4 pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, #C8A96A 1px, transparent 1px)', backgroundSize: '50px 50px' }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#A8894F] text-xs tracking-widest uppercase font-medium mb-3">Our Work</p>
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-[#0A0A0A] leading-tight">
            Recent{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#A8894F] bg-clip-text text-transparent">
              Installations
            </span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-xl mx-auto text-sm md:text-base">
            A snapshot of our electrical installation work across Sleaford and Lincolnshire.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
          {images.map((img, i) => (
            <GalleryCard key={img.alt} {...img} index={i} />
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="https://share.google/wBdMPYxVfjdefwkN9"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#A8894F] hover:text-[#C8A96A] text-sm transition-colors duration-300 cursor-pointer border border-[#C8A96A]/30 hover:border-[#C8A96A] px-5 py-2.5 rounded-full"
          >
            <i className="ri-google-fill" />
            See all our reviews &amp; photos on Google
            <i className="ri-external-link-line text-xs" />
          </a>
        </div>
      </div>
    </section>
  );
}
